import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { materialsApi, type Material, type MaterialType } from '@/api/materials';
import { MaterialReader, coverFor } from './MaterialsWidget';
import { MBlock, MChips, MEmpty, MRow, MTitle } from '@/components/mobile/MobileKit';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Modal } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';

/**
 * «Материалдар» — своя страница вместо ленты на главной.
 *
 * ⚠️ ПОЧЕМУ ПОЯВИЛАСЬ. В макете это отдельный экран: заголовок, фишки-фильтры,
 * крупная карточка сверху и список «Келесі» мелкими строками. До 24.08.2026
 * страницы не существовало вовсе, а ссылки на неё вели в 404 — заказчик поймал
 * это 23.08.2026, и тогда ленту просто показали внутри «Сабақтар».
 *
 * ⚠️ ФИШКИ ТОЛЬКО ПОД ТО, ЧТО ЕСТЬ. В макете их пять (видео, speaking, статья,
 * подкаст), но материал у нас бывает лишь двух видов: ролик и статья. Рисовать
 * фильтр, за которым пусто, — тот же обман, что и ссылка в 404.
 */
export function StudentMaterialsPage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState<'all' | MaterialType>('all');
  const [active, setActive] = useState<Material | null>(null);

  const materials = useQuery({
    queryKey: ['student', 'materials'],
    queryFn: materialsApi.listForStudent,
  });

  const items = useMemo(
    () => (materials.data ?? []).filter((m) => filter === 'all' || m.type === filter),
    [materials.data, filter],
  );

  // Показываем только те фишки, за которыми есть материалы.
  const chips = useMemo(() => {
    const all = materials.data ?? [];
    const out: { key: 'all' | MaterialType; label: string }[] = [
      { key: 'all', label: t('materials.filterAll') },
    ];
    if (all.some((m) => m.type === 'video')) out.push({ key: 'video', label: t('materials.filterVideo') });
    if (all.some((m) => m.type === 'article')) out.push({ key: 'article', label: t('materials.filterArticle') });
    return out;
  }, [materials.data, t]);

  const open = async (m: Material) => {
    setActive(m);
    try {
      await materialsApi.markViewed(m.id);
      await materials.refetch();
    } catch {
      /* открытие материала не должно зависеть от начисления очков */
    }
  };

  const [lead, ...rest] = items;

  const viewer = (
    <Modal open={active !== null} onClose={() => setActive(null)} title={active?.title} size="xl">
      {active && <MaterialReader material={active} />}
    </Modal>
  );

  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle title={t('materials.title')} subtitle={t('materials.subtitle')} back="/app/lessons" />

        {chips.length > 1 && (
          <MChips chips={chips} active={filter} onChange={(k) => setFilter(k)} />
        )}

        {materials.isLoading ? (
          <div className="h-44 animate-pulse rounded-[26px] bg-surface-container-low" />
        ) : items.length === 0 ? (
          <MEmpty icon="video_library" title={t('materials.empty')} hint={t('materials.emptyHint')} />
        ) : (
          <>
            {/* Крупная карточка сверху — как в макете. */}
            <button type="button" onClick={() => void open(lead)} className="block w-full text-left">
              <div className="relative h-[184px] overflow-hidden rounded-[26px] bg-surface-container-low">
                <Cover material={lead} />
                {!lead.viewed && (
                  <span className="absolute left-3 top-3 rounded-full bg-primary px-2.5 py-1 text-[10.5px] font-bold tracking-[0.06em] text-on-primary">
                    {t('materials.new')}
                  </span>
                )}
{/* ⚠️ ПЛАШКА «БАЛЛЫ: N» УБРАНА 10.09.2026 (разбаловка). Очки за
                    просмотр материала больше не начисляются нигде — очко на
                    платформе теперь значит «оценка за урок», и ничего кроме.
                    Плашка обещала ученику то, чего он не получит: открыл
                    материал и увидел, что очков не прибавилось.
                    Колонка materials.points в базе осталась — это единственная
                    запись прежнего замысла, — но показывать её нельзя. */}
              </div>
              <div className="mt-3 px-0.5 text-[15px] font-semibold leading-tight text-on-surface">
                {lead.title}
              </div>
              {lead.description && (
                <div className="mt-0.5 line-clamp-1 px-0.5 text-[11.5px] text-on-surface-variant">
                  {lead.description}
                </div>
              )}
            </button>

            {rest.length > 0 && (
              <>
                <div className="px-0.5 pt-2 font-display text-[15px] font-bold text-on-surface">
                  {t('materials.next')}
                </div>
                <MBlock className="py-1">
                  {rest.map((m, i) => (
                    <MRow
                      key={m.id}
                      last={i === rest.length - 1}
                      dim={m.viewed}
                      onClick={() => void open(m)}
                      leading={
                        <span className="h-[46px] w-[78px] flex-none overflow-hidden rounded-xl bg-surface-container-high">
                          <Cover material={m} />
                        </span>
                      }
                      title={m.title}
                      meta={m.description ?? t(`materials.type.${m.type}`)}
                      trailing={<span />}
                    />
                  ))}
                </MBlock>
              </>
            )}
          </>
        )}
        {viewer}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-display">{t('materials.title')}</h1>
        <p className="mt-1 text-on-surface-variant">{t('materials.subtitle')}</p>
      </div>
      {chips.length > 1 && (
        <div className="flex gap-2">
          {chips.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setFilter(c.key)}
              className={cn(
                'rounded-pill px-4 py-2 text-sm font-semibold transition',
                filter === c.key ? 'bg-primary text-on-primary' : 'bg-surface-container-low text-on-surface',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <Card>
          <p className="text-on-surface-variant">{t('materials.empty')}</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => void open(m)}
              className={cn(
                'overflow-hidden rounded-card bg-surface-container-low text-left ghost-border transition hover:shadow-ambient',
                m.viewed && 'opacity-60',
              )}
            >
              <div className="relative aspect-video w-full bg-surface-container">
                <Cover material={m} />
              </div>
              <div className="p-3">
                <p className="line-clamp-2 text-sm font-semibold leading-tight text-on-surface">{m.title}</p>
                {m.description && (
                  <p className="mt-1 line-clamp-2 text-xs text-on-surface-variant">{m.description}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
      {viewer}
    </div>
  );
}

function Cover({ material }: { material: Material }) {
  const cover = coverFor(material);
  if (!cover) {
    return (
      <span className="grid h-full w-full place-items-center bg-info-container text-info">
        <span className="material-symbols-outlined text-[20px]">article</span>
      </span>
    );
  }
  return (
    <img
      src={cover}
      alt=""
      loading="lazy"
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      className="h-full w-full object-cover"
    />
  );
}
